import React, { useEffect, useState } from 'react';
import { useTypedTranslation } from '@app/hooks';
import { Divider, Menu, MenuItem } from '@material-ui/core';
import { IUserData, TranslationsTypes } from '@app/models';
import { basicService, kaseyaOneService } from '@app/services';
import { PasslyButton, PageLoader, confirm } from '@app/components';
import {
    genericErrorToast,
    localStore
} from '@app/helpers';
import { handleSetSnackbar } from '@app/store/actions/snackbar.actions';
import { useDispatch } from 'react-redux';
import Icon from '@app/components/Icon';
import { signOut } from '@app/store/actions';
import { AxiosError, AxiosResponse } from 'axios';
import { KASEYA_ONE_TOKEN } from '@app/constants';
import { PasslyTooltip } from '@app/componentsV2';

interface IProps {
    user: IUserData;
    setKaseyaOneToken: Function;
}

const RETURN_ENABLE_STATE = 'k1_enable_user';

export const UserMenu: React.SFC<IProps> = ({ user, setKaseyaOneToken }) => {
    const { translation } = useTypedTranslation();
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [isUserKaseyaOneEnabled, setIsUserKaseyaOneEnabled] = useState(false);
    const [isKaseyaOneForTenantEnabled, setIsKaseyaOneForTenantEnabled] = useState(false);
    const [isShowLoader, setIsShowLoader] = useState(false);
    const { error } = { error: '' };
    const queryCodeString = basicService.getQueryVariable('code');
    const queryStateString = basicService.getQueryVariable('state');
    const queryErrorString = basicService.getQueryVariable('error');

    const dispatch = useDispatch();

    const handleGetTenantKaseyaOneStatusSuccess = (response: AxiosResponse) => {
        const enabled: boolean = response.data?.enabled;

        setIsKaseyaOneForTenantEnabled(enabled);
    };

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        kaseyaOneService.getKaseyaOneOrgStatus(handleError, handleGetTenantKaseyaOneStatusSuccess);
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const goToProfilePage = () => {
        handleClose();

        basicService.redirect('/profile');
    };

    const getUserKaseyaOneStatusSuccess = (response: AxiosResponse) => {
        const enabled: boolean = response.data?.enabled;

        setIsUserKaseyaOneEnabled(enabled);
    };

    const userEnableSuccess = (response: AxiosResponse) => {
        const kaseyaOnetoken = response.data?.k1_access_token;

        if (kaseyaOnetoken) {
            localStore.setItem(KASEYA_ONE_TOKEN, kaseyaOnetoken);
            setKaseyaOneToken(kaseyaOnetoken);
        }

        setIsShowLoader(false);
        setIsUserKaseyaOneEnabled(true);
        dispatch(handleSetSnackbar({ show: true, message: translation('KASEYA_ONE_USER_IS_ENABLED'), severity: 'success' }));
    };

    const userDisableSuccess = () => {
        localStore.removeItem(KASEYA_ONE_TOKEN);
        setKaseyaOneToken('');
        setIsShowLoader(false);
        setIsUserKaseyaOneEnabled(false);
        dispatch(handleSetSnackbar({ show: true, message: translation('KASEYA_ONE_USER_IS_DISABLED'), severity: 'success' }));
    };

    const handleGetRedirectUrlSuccess = (response: AxiosResponse) => {
        const redirectUrl: string = response.data?.redirectUrl;

        if (redirectUrl) {
            window.location.href = redirectUrl;
        }
    };

    const getKaseyaOneRedirectUrlSuccess = (response: AxiosResponse) => {
        const redirectUrl: string = response.data?.url;

        if (redirectUrl) {
            window.location.href = redirectUrl;
        }
    };

    const handleError = (error: AxiosError<any>) => {
        const errorKey = error.response?.data.error_description || (error.response?.data.error?.toUpperCase() as TranslationsTypes);

        setIsShowLoader(false);

        if (errorKey) {
            dispatch(handleSetSnackbar({ show: true, message: translation(errorKey), severity: 'error' }));
        } else {
            genericErrorToast(error);
        }
    };

    const handleToggleKaseyaOneComplete = () => {
        if (isUserKaseyaOneEnabled) {
            handleClose();
            handleDisableKaseyaOne();
        } else {
            setIsShowLoader(true);
            kaseyaOneService.getRedirectUserUrl(handleError, getKaseyaOneRedirectUrlSuccess);
        }
    };

    const onSignOutClick = () => dispatch(signOut(false));

    useEffect(() => {
        if (queryStateString === RETURN_ENABLE_STATE && (queryCodeString || queryErrorString)) {
            const data = queryCodeString ? { code: queryCodeString } : { error: queryErrorString };

            setIsShowLoader(true);
            kaseyaOneService.enableUserKaseyaOne(data, handleError, userEnableSuccess);
        }
    }, []);

    useEffect(() => {
        if (error) {
            const errorMessage = translation(error as TranslationsTypes);

            dispatch(handleSetSnackbar({ show: true, message: errorMessage, severity: 'error' }));
        }
    }, [error]);

    useEffect(() => {
        if (isKaseyaOneForTenantEnabled) {
            kaseyaOneService.getUserKaseyaOneStatus(handleError, getUserKaseyaOneStatusSuccess);
        }
    }, [isKaseyaOneForTenantEnabled]);

    const handleDisableKaseyaOne = () => {
        confirm(
            translation('KASEYA_ONE_DISABLE_CONFIRMATION_DESCRIPTION'),
            translation('YES'),
            translation('NO'),
            translation('KASEYA_ONE_DISABLE_CONFIRMATION_TITLE')
        ).then(
            () => {
                setIsUserKaseyaOneEnabled(!isUserKaseyaOneEnabled);
                kaseyaOneService.disableUserKaseyaOne(handleError, userDisableSuccess);
            },
            () => {}
        );
    };
    return user ? (
        <>
            <PasslyTooltip title={translation('NAVBAR_PROFILE_TOOLTIP')}>
                <span>
                    <PasslyButton
                        id='profileIconButton'
                        className={`usermenu__trigger ${anchorEl ? 'navbar__content-active' : ''}`}
                        onClick={handleClick}
                    >
                        <Icon icon='profileIco' width='24px' height='24px' />
                        <span className='usermenu__label'>{user.displayName}</span>
                    </PasslyButton>
                </span>
            </PasslyTooltip>
            <Menu
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={handleClose}
                className='usermenu'
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right'
                }}
                getContentAnchorEl={null}
            >
                <div className='usermenu__head'>
                    <div className='usermenu__avatar'>
                        <Icon className='usermenu__avatar-component' icon='profileIco' />
                        <div>
                            <div className='usermenu__name'>{user.displayName}</div>
                            <div className='usermenu__email'>{user.email}</div>
                        </div>
                    </div>
                </div>
                <Divider />
                <MenuItem className='usermenu__row' onClick={goToProfilePage}>
                    {translation('USER_MENU_SETTINGS')}
                </MenuItem>
                <Divider />

                {isKaseyaOneForTenantEnabled && (
                    <MenuItem className='usermenu__row' onClick={handleToggleKaseyaOneComplete}>
                        {isUserKaseyaOneEnabled
                            ? translation('DISSOCIATE_LOG_IN_WITH_KASEYA_ONE')
                            : translation('ASSOCIATE_LOG_IN_WITH_KASEYA_ONE')}
                    </MenuItem>
                )}
                <MenuItem className='usermenu__signout' onClick={onSignOutClick}>
                    {translation('USER_MENU_LOG_OUT')}
                </MenuItem>
            </Menu>
            {isShowLoader && <PageLoader useOverlay={true} />}
        </>
    ) : null;
};
